import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

interface SubscriptionStatus {
    effectiveStatus: 'trial' | 'active' | 'grace' | 'readonly' | 'expired';
    daysRemaining: number;
    message?: string;
    subscription?: {
        id: string;
        status: string;
        trialEndsAt?: string;
        endsAt?: string;
        plan?: {
            name: string;
        };
    };
}

interface SubscriptionContextType {
    status: SubscriptionStatus | null;
    loading: boolean;
    isReadOnly: boolean;
    isExpired: boolean;
    canWrite: boolean;
    refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
    status: null,
    loading: true,
    isReadOnly: false,
    isExpired: false,
    canWrite: true,
    refresh: async () => { },
});

export function useSubscription() {
    return useContext(SubscriptionContext);
}

const API_URL = import.meta.env.VITE_API_URL;

export function SubscriptionProvider({ children }: { children: ReactNode }) {
    const { firebaseUser } = useAuth();
    const { tenant } = useTenant();
    const [status, setStatus] = useState<SubscriptionStatus | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchStatus = async () => {
        if (!firebaseUser || !tenant) {
            setLoading(false);
            return;
        }

        try {
            const token = await firebaseUser.getIdToken();
            const response = await fetch(`${API_URL}/subscription/status`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setStatus(data);
            }
        } catch (error) {
            console.error('Failed to fetch subscription status:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, [firebaseUser, tenant]);

    const isReadOnly = status?.effectiveStatus === 'readonly';
    const isExpired = status?.effectiveStatus === 'expired';
    const canWrite = status?.effectiveStatus === 'trial' ||
        status?.effectiveStatus === 'active' ||
        status?.effectiveStatus === 'grace';

    return (
        <SubscriptionContext.Provider value={{
            status,
            loading,
            isReadOnly,
            isExpired,
            canWrite,
            refresh: fetchStatus,
        }}>
            {children}
        </SubscriptionContext.Provider>
    );
}
